import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { useCompanyInfo, useReviews } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function SmedejernshegnPage() {
  const companyInfo = useCompanyInfo();
  const reviews = useReviews();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const images = [
    { src: "/_assets/img20251015162947.jpg", title: "Sort smedejernslåge og hegn ved træbeklædning" },
    { src: "/_assets/img20251015162706.jpg", title: "Sort smedejernslåge åben ved havegang" },
    { src: "/_assets/285327761-550001316500328-211016002518253666-n.jpg", title: "smedejernshegn" },
    { src: "/_assets/285582236-550001266500333-8021079231710675885-n.jpg", title: "Smedejernshegn pris" },
    { src: "/_assets/smedejernshegn.jpg", title: "Smedejernshegn og låger" },
    { src: "/_assets/smedejernshegn-og-l-ger.jpg", title: "Smedejernshegn i Danmark" },
    { src: "/_assets/smedejernshegn-pris.jpg", title: "smedejernslåge til indkørsel" },
  ];

  return (
    <>
      <SEO
        title="Smedejernshegn i Svendborg, Odense og hele Danmark her"
        description="Med smedejernshegn får du både en praktisk og elegant løsning, der holder. Vi holder til på Fyn og leverer til hele Danmark. Få et tilbud."
        ogImage="/_assets/480188274-1283354403152757-3860986236524628895-n.jpg"
      />

      {/* Hero */}
      <section className="relative py-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={imageUrl("/_assets/480188274-1283354403152757-3860986236524628895-n.jpg", "width=1600,quality=80,format=auto")}
            alt="Smedejernshegn"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#004fa4]/20 text-[#c2ccdf] border border-[#004fa4]/30">
              <i className="fa-sharp fa-solid fa-hammer" aria-hidden="true"></i>
              <span>Klassisk & elegant</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Smedejernshegn i Odense og resten af Danmark
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed font-normal">
              Et smedejernshegn er en elegant og holdbar måde at afgrænse din ejendom på – uanset om du er privat boligejer eller erhvervskunde.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">Eksklusivt håndværk</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
                  Holdbare hegn i smedejern
                </h2>
              </div>
              <p className="text-slate-700 leading-relaxed text-base">
                Et smedejernshegn er meget mere end blot en praktisk løsning. Det er en elegant og holdbar måde at afgrænse din ejendom på – uanset om du er privat boligejer eller erhvervskunde. Hos Hovendals ApS har vi mange års erfaring med at designe og fremstille smedejernshegn, der passer perfekt til både funktion, æstetik og omgivelser.
              </p>
              <p className="text-slate-700 leading-relaxed text-base">
                Du får en løsning, som er skræddersyet til dine behov, og vi står klar til at hjælpe dig – uanset hvor i Danmark du holder til. Vi oplever især stor efterspørgsel i byer som Odense, Svendborg og andre steder på Fyn, men vi leverer naturligvis til hele landet.
              </p>

              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="px-6 py-3 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all flex items-center gap-2"
                >
                  <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
                  <span>Ring os op: {companyInfo.phone}</span>
                </a>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="px-6 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all flex items-center gap-2"
                >
                  <i className="fa-sharp fa-solid fa-envelope" aria-hidden="true"></i>
                  <span>Skriv en mail</span>
                </a>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl border-4 border-white bg-slate-100">
              <img
                src={imageUrl("/_assets/smedejernshegn.jpg", "width=800,quality=85,format=auto")}
                alt="Smedejernshegn og låger"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Section: Hvorfor vælge Hovendals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200/80 space-y-4">
              <h3 className="text-2xl font-bold text-slate-900">
                Derfor vælger kunder smedejernshegn fra Hovendals ApS
              </h3>
              <p className="text-slate-700 leading-relaxed text-base">
                Når du vælger et smedejernshegn fra Hovendals ApS, får du ikke bare et flot design. Du investerer i et produkt, der holder i mange år frem. Vores hegn er fremstillet i robust smedejern, som tåler vind og vejr og kræver minimal vedligeholdelse.
              </p>
              <p className="text-slate-700 leading-relaxed text-base">
                Du kan få dit smedejernshegn i klassisk stil med dekorative detaljer – eller i et mere enkelt og moderne udtryk. Uanset stilretning sørger vi for, at dit hegn komplementerer bygningen og skaber en stærk helhed i det visuelle udtryk.
              </p>
              <p className="text-slate-700 leading-relaxed text-base">
                Vi hjælper dig hele vejen – fra første idé til færdigmonteret hegn. Du får en løsning, der både fungerer og fremstår professionel.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200/80 space-y-4">
              <h3 className="text-2xl font-bold text-slate-900">
                Smedejernshegn og låger i ét samlet design
              </h3>
              <p className="text-slate-700 leading-relaxed text-base">
                Et smedejernshegn bliver kun bedre, når det kombineres med en <Link to="/smedejernslaage" className="text-[#004fa4] font-bold hover:underline">smedejernslåge</Link>, der matcher i både design og kvalitet. Derfor tilbyder vi også låger, der kan bruges som indgangsport eller indkørsel, og som bliver designet sammen med dit hegn.
              </p>
              <p className="text-slate-700 leading-relaxed text-base">
                Du bestemmer selv, hvilken type låge du ønsker, og vi sørger for alt fra produktion til montering – så du får en komplet løsning.
              </p>
              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all"
                >
                  <span>Få smedejernshegn nu</span>
                  <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Section: Designs og rådgivning */}
          <div className="bg-slate-900 text-white rounded-2xl p-8 sm:p-12 mb-16 space-y-6">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
              Find forskellige designs af smedejernshegn i Odense, Svendborg og hele Danmark
            </h3>
            <p className="text-slate-300 leading-relaxed text-base">
              Hos Hovendals ApS er intet standard. Vi designer smedejernshegn helt fra bunden, så det passer til netop din bolig eller virksomhed. Vi tilbyder både ensartede linjer, rundede former og håndsmedede detaljer – og vi rådgiver dig gerne i forhold til arkitektur og omgivelser.
            </p>
            <p className="text-slate-300 leading-relaxed text-base">
              Du er velkommen til at kontakte os uanset, hvor i Danmark du bor. Vi leverer til både private og erhvervskunder i hele landet og oplever især stor interesse i Odense, Svendborg og på resten af Fyn.
            </p>
          </div>

          {/* Reviews snippet */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">
              Kundeudtalelser
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((rev, idx) => (
                <div key={idx} className="p-6 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-1 text-[#004fa4] text-xs mb-2">
                      {[...Array(rev.rating)].map((_, i) => (
                        <i key={i} className="fa-sharp fa-solid fa-star" aria-hidden="true"></i>
                      ))}
                    </div>
                    <div className="font-bold text-slate-900 text-sm mb-2">{rev.title}</div>
                    <p className="text-xs text-slate-600 italic leading-relaxed mb-4">"{rev.text}"</p>
                  </div>
                  <div className="text-xs font-bold text-slate-800">{rev.author}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Billedgalleri */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              Smedejernshegn billeder
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              Se udvalgte eksempler på smedejernshegn og smedejernslåger monteret i Danmark.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative h-48 rounded-xl overflow-hidden bg-slate-100 cursor-pointer shadow-sm hover:shadow-md transition-all"
                >
                  <img
                    src={imageUrl(img.src, "width=400,quality=80,format=auto")}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                    <span className="p-2 rounded-full bg-[#143463]/90 text-white text-xs">
                      <i className="fa-sharp fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pris & tilbud */}
          <div className="border border-slate-200 rounded-2xl p-8 sm:p-10 bg-slate-50 space-y-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              Pris på et smedejernshegn
            </h2>
            <p className="text-slate-700 leading-relaxed text-base">
              Når du overvejer at investere i et nyt hegn, er prisen naturligvis vigtig. Men smedejernshegn pris afhænger af mange faktorer – blandt andet:
            </p>
            <ul className="list-disc list-inside space-y-1.5 text-slate-700 text-base font-medium">
              <li>Længde</li>
              <li>Højde</li>
              <li>Design</li>
              <li>Materialevalg</li>
            </ul>
            <p className="text-slate-700 leading-relaxed text-base">
              Måske du også ønsker én eller flere smedejernslåger inkluderet. Hos Hovendals ApS tilbyder vi uforpligtende rådgivning og prisoverslag baseret på dine ønsker. Vi hjælper dig med at finde en løsning, der matcher både stil og budget. Fordi vi laver alt på mål, kan vi tilpasse os stort set alle behov.
            </p>
            <div className="pt-2">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Få et godt tilbud på smedejernshegn</h3>
              <p className="text-slate-700 text-sm mb-4">
                Ønsker du at få et uforpligtende tilbud på smedejernshegn Så er du velkommen til at kontakte os på <a href={`tel:${companyInfo.phoneFormatted}`} className="text-[#004fa4] font-bold hover:underline">40555599</a> eller send os en mail på <a href={`mailto:${companyInfo.email}`} className="text-[#004fa4] font-bold hover:underline">{companyInfo.email}</a> også vender vi tilbage til dig hurtigst muligt.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="px-6 py-2.5 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all"
                >
                  Ring os op
                </a>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="px-6 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all"
                >
                  Skriv en mail
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <QuoteSection />

      {/* Lightbox modal */}
      <LightboxModal
        images={images}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={(index) => setLightboxIndex(index)}
      />
    </>
  );
}
